<template>
  <Form
    id="content-page-form"
    :form-id="FinancialFormIDs.CASHLESS_PAY_FORM"
    :schema="schema"
    :initialValues="initialValues"
    @onValidateSuccess="handleValidationSuccess"
    @onValidateFail="handleValidationFail"
    :loading="saving"
    ref="formComponent"
  >
    <template v-slot="{ isFormValid, form }">
      <CashlessPayGrid />
    </template>
  </Form>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useCashlessPay } from './composables';
import useGlobalToast from '@centric-os/components/src/core/messages/toast/composables/use-global-toast';
import CashlessPayGrid from './cashless-pay-grid/CashlessPayGrid.vue';
import { onUnmounted, ref } from 'vue';
import type { Form } from '@centric-os/components/src/composed/forms/form';
import { useEventBus } from '@vueuse/core';
import type { ApiResponseError, SitePrivateConfig, SitePublicConfig } from '@centric-os/types';
import { storeToRefs } from 'pinia';
import { useSiteStore } from '@centric-os/stores/sites/use-site-store';
import { useStoreAPIAction } from '@centric-os/stores/plugins/api';
import { FinancialFormIDs } from '../enums';
import {
  useCashlessPayStore,
  useFinancialConfigStore,
  useSiteConfigStore,
} from '../../../../routes';
import { useCashlessPaySchema } from '../../../../composables';

const props = defineProps({
  isSiteConfig: {
    type: Boolean,
    required: true,
  },
});

// Set localization
const { t } = useI18n();

// Define refs for form component
const formComponent = ref<InstanceType<typeof Form>>();

// Set financial config store
const fConfigStore = useFinancialConfigStore();
const { editCashlessPay } = storeToRefs(fConfigStore);

// Set cashless pay store
const cashlessPayStore = useCashlessPayStore();
const { loading: saving } = useStoreAPIAction(cashlessPayStore, 'saveCashlessPay');

// Set cashless pay schema
const { schema } = useCashlessPaySchema();

// Set cashless pay composable
const { initialValues, mapSitePrivateConfigPayload, mapSitePublicConfigPayload } = useCashlessPay();

// Set global toast
const { showErrorToast, showSuccessToast } = useGlobalToast();

// Submit form
const save = async (): Promise<void> => {
  formComponent.value?.validate();
};

// Handle validation success
const { on: onCashlessPay, off: offCashlessPay } = useEventBus<void>(
  `save-cashless-pay-${props.isSiteConfig ? 'site' : 'station'}`,
);
// Set the listener for validate and submit form
onCashlessPay(save);

// Handle validation success
const handleValidationSuccess = async (form: any): Promise<void> => {
  let privatePayload: SitePrivateConfig = null;
  let publicPayload: SitePublicConfig = null;

  let id: string = null;
  if (props.isSiteConfig) {
    const siteStore = useSiteStore();
    const { site } = storeToRefs(siteStore);
    id = site.value.id as string;

    privatePayload = mapSitePrivateConfigPayload(form);
    publicPayload = mapSitePublicConfigPayload(form);
  } else {
    // Station
  }

  await cashlessPayStore
    .saveCashlessPay(id, props.isSiteConfig, privatePayload, publicPayload)
    .then(async () => {
      if (props.isSiteConfig) {
        const siteConfigStore = useSiteConfigStore();
        await siteConfigStore.fetchSiteConfigurations(id);
      }
      showSuccessToast(t('sites.singleSite.financialConfig.cashlessPay.form.success'));
      editCashlessPay.value = false;
    })
    .catch((error: ApiResponseError) => {
      if (error.status) {
        if (error.status >= 400 && error.status < 500) {
          showErrorToast(error?.error ? error?.error : (error?.message as string));
        } else {
          showErrorToast(t('errorMessages.genericApiError'));
        }
      }
    });
};

// Handle validation fail
const handleValidationFail = (): void => {
  showErrorToast(t('sites.singleSite.financialConfig.cashlessPay.form.validationFailed'));
};

// Unmount listeners
onUnmounted(() => {
  offCashlessPay(save);
});
</script>
